import React from 'react';
import ReportService from '../service/ReportService';
import ReportingUtils, { markBrsrExportNumericTableCells } from './ReportingUtils';
import Utils from './Utils';
import { ReportPageType } from '../constants/common';

export const QUES_CODE_PREFIX = 'BRSR-';

/** Wraps combined page HTML and marks numeric-only table cells for right alignment in Word/PDF export */
const wrapBrsrExportHtml = (allPagesHTML) =>
	markBrsrExportNumericTableCells(`<div class="ETA_card_center">${allPagesHTML.join('')}</div>`);

/**
 * Formats text with line breaks into paragraph elements
 * @param {string} text - Text to format
 * @returns {JSX.Element} Formatted text with paragraphs
 */
export const getFormattedTextEmbeddedinLITag = (text) => {
	if (!text) return <></>;
	const lines = text.split('\n');
	const paras = [];
	lines.forEach((line, index) => {
		if (line.trim()) {
			paras.push(<p key={index} className='my-2'>{line}</p>);
		}
	});
	return (
		<div>{paras}</div>
	);
};

/**
 * Renders a question and answer pair
 * @param {string} question - Question text
 * @param {string} answer - Answer text
 * @param {number|string|null} slNo - Serial number (optional)
 * @returns {JSX.Element} Question-answer component
 */
export const renderQuestionAnswer = (question, answer, slNo = null) => {
	const formattedAnswer = getFormattedTextEmbeddedinLITag(answer || '');
	return (
		<div className='brsr-list-item'>
			{slNo && <span className="count">{slNo}</span>}
			<b>{question}</b>
			{formattedAnswer}
		</div>
	);
};

/**
 * Renders a question and answer pair with table_list01 styling
 * @param {string} question - Question text
 * @param {string} answer - Answer text
 * @param {number|string|null} slNo - Serial number (optional)
 * @returns {JSX.Element} Question-answer component with table_list01 class
 */
export const renderQuestionAnswerType2 = (question, answer, slNo = null) => {
	const formattedAnswer = getFormattedTextEmbeddedinLITag(answer || '');
	return (
		<div className='table_list01'>
			{slNo && <span className="count">{slNo}</span>}
			<b>{question}</b>
			{formattedAnswer}
		</div>
	);
};

/**
 * Normalizes cell key to string
 * @param {any} cell - Cell value
 * @returns {string} Normalized cell key
 */
export const normalizeCellKey = (cell) => String(cell ?? '');

/**
 * Sorts row keys (handles numeric and string keys)
 * @param {string[]} keys - Array of keys to sort
 * @returns {string[]} Sorted array of keys
 */
export const sortRowKeys = (keys) => [...keys].sort((a, b) => {
	const na = Number(a);
	const nb = Number(b);
	if (a === '' || b === '') return String(a).localeCompare(String(b));
	if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
	return String(a).localeCompare(String(b));
});

/**
 * Normalizes question key by removing prefix
 * @param {string} k - Key to normalize
 * @returns {string} Normalized key without prefix
 */
export const normKey = (k) => String(k ?? '').replace(QUES_CODE_PREFIX, '');

/**
 * Builds merged rows for table rendering
 * @param {Array} answers - Array of answer objects
 * @param {string} qIndex - Question index
 * @param {Array} colNos - Column numbers array
 * @returns {Object} Object with rowKeys and groupedDataByCell
 */
export const buildMergedRows = (answers, qIndex, colNos) => {
	// Simplified: ignore "cell" and always return a single logical row.
	// Map columns by normalized keys derived from qIndex + colNos.
	const rowId = `${qIndex}-ROW-0`;
	const keyToAnswer = {};
	(answers || []).forEach(a => {
		keyToAnswer[normKey(a.key)] = a;
	});
	const selected = [];
	(colNos || []).forEach(colNo => {
		if (colNo === '') return; // skip serial/index column
		const colKey = normKey(`${QUES_CODE_PREFIX}${qIndex}${colNo}`);
		const ans = keyToAnswer[colKey];
		if (ans) selected.push({ ...ans });
	});
	const rowValues = selected.length > 0 ? selected : (answers || []).map(v => ({ ...v }));
	return { rowKeys: [rowId], groupedDataByCell: { [rowId]: rowValues } };
};

/**
 * Like {@link Utils.formatYAxisValue} (grouping + decimals) but does not strip trailing zeros,
 * so 28.70 stays "28.70". Integers stay unscaled (e.g. 1,000).
 * @param {number} num
 * @param {number} minFraction - minimum digits after the decimal for non-integers (default 2)
 */
const formatBrsrNumberLikeYAxis = (num, minFraction = 2) => {
	if (!Number.isFinite(num)) return '';
	if (num === 0) return '0';
	if (Number.isInteger(num)) {
		return num.toLocaleString('en-US', {
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
			useGrouping: true
		});
	}
	return num.toLocaleString('en-US', {
		minimumFractionDigits: minFraction,
		maximumFractionDigits: 20,
		useGrouping: true
	});
};

/**
 * Formats plain numeric BRSR cell values with thousand separators (en-US), same idea as DataView
 * {@link Utils.formatYAxisValue}, but keeps at least minFraction decimal places for non-integers.
 * Non-numeric and HTML text answers are returned sanitized and unchanged.
 * @param {any} rawVal - Raw cell value
 * @param {number} [minFraction=2]
 * @returns {string} Display string
 */
export const formatBrsrNumericCellValue = (rawVal, minFraction = 2) => {
	if (rawVal == null || rawVal === '') return '';

	if (typeof rawVal === 'number') {
		if (!Number.isFinite(rawVal)) return '';
		return formatBrsrNumberLikeYAxis(rawVal, minFraction);
	}

	const str = typeof rawVal === 'string' ? rawVal.replace(/<[^>]*>/g, '').trim() : String(rawVal);
	if (str === '') return '';

	const cleaned = str.replace(/,/g, '');
	if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return str;

	const num = parseFloat(cleaned);
	if (!Number.isFinite(num)) return str;
	return formatBrsrNumberLikeYAxis(num, minFraction);
};

/**
 * Gets display value for a cell, sanitizing HTML and applying numeric formatting
 * @param {any} rawVal - Raw cell value
 * @param {number} [minFraction=2]
 * @returns {string} Sanitized display value
 */
export const getCellDisplayVal = (rawVal, minFraction = 2) =>
	formatBrsrNumericCellValue(rawVal, minFraction);

/**
 * Renders Type 1 table answer for current year only
 * @param {Object} pageData - Page data object with currentYear
 * @param {number} pageId - Page ID (for unique keys)
 * @param {string} qIndex - Question index
 * @param {Array} colNos - Column numbers array
 * @returns {JSX.Element} Table rows
 */
export const renderType1TableAnswerForCurrentYear = (pageData, pageId, qIndex, colNos) => {
	const d = pageData || {};
	if (!d['currentYear'] || !d['currentYear'][QUES_CODE_PREFIX + qIndex] || d['currentYear'][QUES_CODE_PREFIX + qIndex].keyValues?.length === 0) {
		return <tr></tr>;
	}
	const answers = d['currentYear'][QUES_CODE_PREFIX + qIndex].keyValues;
	const { rowKeys: rows, groupedDataByCell } = buildMergedRows(answers, qIndex, colNos);
	const targetColNorm = (colNo) => normKey(`${QUES_CODE_PREFIX}${qIndex}${colNo}`);
	return (
		<>
			{rows?.map((row, rIndex) => (
				<tr key={`page${pageId}Table-row-cy-${qIndex}-${rIndex}-${row}`}>
					{colNos?.map((colNo, cIndex) => {
						if (colNo === '') return <td key={`page${pageId}Table-row-cy-${qIndex}-${rIndex}-${cIndex}`}>{rIndex + 1}</td>;
						const colNorm = targetColNorm(colNo);
						const entry = groupedDataByCell[row]?.find(val => normKey(val.key) === colNorm);
						return (
							<td key={`page${pageId}Table-row-cy-${qIndex}-${rIndex}-${cIndex}`}>
								{getCellDisplayVal(entry?.value)}
							</td>
						);
					})}
				</tr>
			))}
		</>
	);
};

/**
 * Renders Type 1 table answer for page1 (single page data structure)
 * @param {Object} page1 - Page 1 data object
 * @param {string} qIndex - Question index
 * @param {Array} colNos - Column numbers array
 * @returns {JSX.Element} Table rows
 */
export const renderType1TableAnswer = (page1, qIndex, colNos) => {
	const d = page1 || {};
	if (!d[QUES_CODE_PREFIX + qIndex] || d[QUES_CODE_PREFIX + qIndex].keyValues?.length === 0) {
		return <tr></tr>;
	}
	const answers = d[QUES_CODE_PREFIX + qIndex].keyValues;
	const { rowKeys: rows, groupedDataByCell } = buildMergedRows(answers, qIndex, colNos);
	const targetColNorm = (colNo) => normKey(`${QUES_CODE_PREFIX}${qIndex}${colNo}`);
	return (
		<>
			{rows?.map((row, rIndex) => (
				<tr key={`page1Table-row-${qIndex}-${rIndex}-${row}`}>
					{colNos?.map((colNo, cIndex) => {
						if (colNo === '') return <td key={`page1Table-row-${qIndex}-${rIndex}-${cIndex}`}>{rIndex + 1}</td>;
						const colNorm = targetColNorm(colNo);
						const entry = groupedDataByCell[row]?.find(val => normKey(val.key) === colNorm);
						return (
							<td key={`page1Table-row-${qIndex}-${rIndex}-${cIndex}`}>
								{getCellDisplayVal(entry?.value)}
							</td>
						);
					})}
				</tr>
			))}
		</>
	);
};

/**
 * Gets single value answer from page data
 * @param {Object} page1 - Page 1 data object
 * @param {string} index - Question index
 * @returns {JSX.Element} Formatted answer or empty fragment
 */
export const getSingleValueAnswer = (page1, index) => {
	const data = page1?.[QUES_CODE_PREFIX + index];
	if (data?.keyValueType === 'SINGLE_VALUE' && data.keyValues[0]?.value) {
		return getFormattedTextEmbeddedinLITag(data.keyValues[0]?.value ?? '');
	}
	return <></>;
};

/**
 * Generates Word document from BRSR report
 * Fetches the report if needed, decodes HTML, combines all pages, and triggers Word download
 * @param {Object} report - Report object (can be partial with just id, or full report)
 * @param {Function} setLoaderDisplay - Function to set loader display state
 * @param {Function} onError - Optional error callback function
 * @returns {Promise<void>}
 */
export const generateBRSRWordFromReport = async (report, setLoaderDisplay, onError) => {
	if (!report || !report.id) {
		const error = new Error('Report ID is required');
		if (onError) onError(error);
		return;
	}

	setLoaderDisplay(true);

	try {
		// Fetch full report if we only have ID or sections are missing
		let fullReport = report;
		if (!report.sections || report.sections.length === 0) {
			const res = await ReportService.getBRSRReport(report.id);
			if (res.status !== 200) {
				throw new Error('Failed to fetch report');
			}
			fullReport = res.data;
		}

		// Sort sections and pages by sortOrder
		fullReport.sections?.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
		fullReport.sections?.forEach(section => {
			section.pages?.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
		});

		// Separate title page from other pages
		let titlePageHTML = null;
		const otherPagesHTML = [];
		
		fullReport.sections?.forEach(section => {
			section.pages?.forEach(page => {
				let pageHTML = null;

				// Decode HTML from htmlContent if html is not available (same logic as ReportingContainer)
				if (page.html) {
					pageHTML = page.html;
				} else if (page.htmlContent) {
					try {
						pageHTML = Utils.decodeBase64Utf8(page.htmlContent);
					} catch (e) {
						console.error('Error decoding HTML content:', e);
						pageHTML = null;
					}
				}

				if (pageHTML) {
					pageHTML = Utils.repairRupeeMojibake(pageHTML);
					// Check if this is the title page (pageType TITLE = 0)
					if (page.type === ReportPageType.TITLE || page.type === 0 || page.type === 'TITLE') {
						// Full-page cover: layout + print styles in ReportingUtils.generatePrintHTML (.brsr-report-cover-page)
						titlePageHTML = `<div class="brsr-report-cover-page">${pageHTML}</div>`;
					} else {
						otherPagesHTML.push(pageHTML);
					}
				}
			});
		});

		if (!titlePageHTML && otherPagesHTML.length === 0) {
			throw new Error('No HTML content found in report pages');
		}

		// Combine pages: title page first (full page), then other pages
		const allPagesHTML = [];
		if (titlePageHTML) {
			allPagesHTML.push(titlePageHTML);
		}
		allPagesHTML.push(...otherPagesHTML);

		// Combine all pages with proper wrapper structure to match CSS expectations
		// The wrapper structure should match what BRSR.scss expects (.ETA_card_center)
		const combinedHTML = wrapBrsrExportHtml(allPagesHTML);

		// Generate filename
		const yearFilter = Utils.getYearFrom(fullReport.period || report.period);
		const fileName = `BRSR_${yearFilter}.docx`;

		// Use Word download functionality (same as old BRSR report)
		ReportingUtils.downloadBRSRReportWord(combinedHTML, fileName);

		setLoaderDisplay(false);
	} catch (error) {
		setLoaderDisplay(false);
		console.error('Error generating BRSR Word document:', error);
		if (onError) {
			onError(error);
		} else {
			throw error;
		}
	}
};

/**
 * Generates PDF from BRSR report using browser print functionality
 * Fetches the report if needed, decodes HTML, combines all pages, and triggers browser print
 * @param {Object} report - Report object (can be partial with just id, or full report)
 * @param {Function} setLoaderDisplay - Function to set loader display state
 * @param {Function} onError - Optional error callback function
 * @returns {Promise<void>}
 */
export const generateBRSRPDFFromReport = async (report, setLoaderDisplay, onError) => {
	if (!report || !report.id) {
		const error = new Error('Report ID is required');
		if (onError) onError(error);
		return;
	}

	setLoaderDisplay(true);

	try {
		// Fetch full report if we only have ID or sections are missing
		let fullReport = report;
		if (!report.sections || report.sections.length === 0) {
			const res = await ReportService.getBRSRReport(report.id);
			if (res.status !== 200) {
				throw new Error('Failed to fetch report');
			}
			fullReport = res.data;
		}

		// Sort sections and pages by sortOrder
		fullReport.sections?.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
		fullReport.sections?.forEach(section => {
			section.pages?.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
		});

		// Separate title page from other pages
		let titlePageHTML = null;
		const otherPagesHTML = [];
		
		fullReport.sections?.forEach(section => {
			section.pages?.forEach(page => {
				let pageHTML = null;

				// Decode HTML from htmlContent if html is not available (same logic as ReportingContainer)
				if (page.html) {
					pageHTML = page.html;
				} else if (page.htmlContent) {
					try {
						pageHTML = Utils.decodeBase64Utf8(page.htmlContent);
					} catch (e) {
						console.error('Error decoding HTML content:', e);
						pageHTML = null;
					}
				}

				if (pageHTML) {
					pageHTML = Utils.repairRupeeMojibake(pageHTML);
					// Check if this is the title page (pageType TITLE = 0)
					if (page.type === ReportPageType.TITLE || page.type === 0 || page.type === 'TITLE') {
						// Full-page cover: layout + print styles in ReportingUtils.generatePrintHTML (.brsr-report-cover-page)
						titlePageHTML = `<div class="brsr-report-cover-page">${pageHTML}</div>`;
					} else {
						otherPagesHTML.push(pageHTML);
					}
				}
			});
		});

		if (!titlePageHTML && otherPagesHTML.length === 0) {
			throw new Error('No HTML content found in report pages');
		}

		// Combine pages: title page first (full page), then other pages
		const allPagesHTML = [];
		if (titlePageHTML) {
			allPagesHTML.push(titlePageHTML);
		}
		allPagesHTML.push(...otherPagesHTML);

		// Combine all pages with proper wrapper structure to match CSS expectations
		// The wrapper structure should match what BRSR.scss expects (.ETA_card_center)
		const combinedHTML = wrapBrsrExportHtml(allPagesHTML);

		// Generate filename
		const yearFilter = Utils.getYearFrom(fullReport.period || report.period);
		const fileName = `BRSR_${yearFilter}.pdf`;

		// Use browser print functionality (same as old BRSR report)
		ReportingUtils.downloadBRSRReportPDFViaPrint(combinedHTML, fileName);

		setLoaderDisplay(false);
	} catch (error) {
		setLoaderDisplay(false);
		console.error('Error generating BRSR PDF:', error);
		if (onError) {
			onError(error);
		} else {
			throw error;
		}
	}
};


/** @param {Object} kv */
const parseType3RowCell = (kv) => {
	if (!kv || kv.cell == null || kv.cell === '') return null;
	const n = parseInt(String(kv.cell).trim(), 10);
	return Number.isFinite(n) ? n : null;
};

/**
 * TYPE_3 rows: one row per distinct keyValue.cell (sorted 1, 2, 3, …). Columns match by key + cell.
 */
const renderType3TableRowsFromBlock = (block, rowKeyPrefix, qIndex, colNos) => {
	if (!block || !Array.isArray(block.keyValues)) {
		return <tr></tr>;
	}
	const dataCols = (colNos || []).filter((c) => c !== '');
	if (dataCols.length === 0) {
		return <tr></tr>;
	}
	const byCol = {};
	dataCols.forEach((colSuf) => {
		const nk = `${qIndex}${colSuf}`;
		byCol[colSuf] = block.keyValues.filter((kv) => normKey(kv.key) === nk);
	});

	const rowNums = new Set();
	dataCols.forEach((colSuf) => {
		byCol[colSuf].forEach((kv) => {
			const n = parseType3RowCell(kv);
			if (n != null) rowNums.add(n);
		});
	});
	const rows = [...rowNums].sort((a, b) => a - b);
	if (rows.length === 0) {
		return <tr></tr>;
	}

	return (
		<>
			{rows.map((cellNum, rIndex) => (
				<tr key={`${rowKeyPrefix}-${qIndex}-${cellNum}`}>
					{colNos.map((colNo, cIndex) => {
						if (colNo === '') {
							return <td key={`${rowKeyPrefix}-${qIndex}-${cellNum}-${cIndex}`}>{rIndex + 1}</td>;
						}
						const entry = byCol[colNo].find((kv) => parseType3RowCell(kv) === cellNum);
						return (
							<td key={`${rowKeyPrefix}-${qIndex}-${cellNum}-${cIndex}`}>
								{getCellDisplayVal(entry?.value)}
							</td>
						);
					})}
				</tr>
			))}
		</>
	);
};

/**
 * TYPE_3_TABLE: column keys repeat per row; rows ordered by keyValue.cell.
 * @param {Object} page1 - Page map (question key → block)
 * @param {number|string} qIndex - Question number (e.g. 23)
 * @param {string[]} colNos - e.g. ['', 'a','b','c','d'] — '' renders S.No.
 * @returns {JSX.Element|null} Fragment of &lt;tr&gt; rows, or null if not TYPE_3_TABLE
 */
export const renderType3TableAnswer = (page1, qIndex, colNos) => {
	const block = page1?.[QUES_CODE_PREFIX + qIndex];
	if (!block || block.keyValueType !== 'TYPE_3_TABLE') {
		return null;
	}
	return renderType3TableRowsFromBlock(block, 'page1Table-type3', qIndex, colNos);
};

/**
 * TYPE_3_TABLE for page data shaped like Principle pages: { currentYear: { 'BRSR-…': { keyValueType, keyValues } } }.
 * Mirrors {@link renderType1TableAnswerForCurrentYear} but with TYPE_3 row alignment.
 * @param {Object} pageData - Page data with currentYear
 * @param {number} pageId - Page id for React keys (e.g. 4)
 * @param {string} qIndex - Question index
 * @param {string[]} colNos - Column suffixes including '' for S.No.
 * @returns {JSX.Element|null} Table rows, or null if not TYPE_3_TABLE on current year block
 */
export const renderType3TableAnswerForCurrentYear = (pageData, pageId, qIndex, colNos) => {
	const block = pageData?.currentYear?.[QUES_CODE_PREFIX + qIndex];
	if (!block || block.keyValueType !== 'TYPE_3_TABLE') {
		return null;
	}
	return renderType3TableRowsFromBlock(block, `page${pageId}Table-type3-cy`, qIndex, colNos);
};